
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutGrid, ArrowLeft, Settings, 
  Package, TrendingUp, AlertCircle, RefreshCw, BarChart3,
  ChevronDown, ChevronUp, Users, List, Home, ShoppingCart,
  User, CreditCard, Layers, Shield, FileText, LayoutList
} from './Icons';
import { BrandLogo } from './BrandLogo';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { TRANSITIONS } from '../constants/theme';
import footerImage from '../assets/iomarket logo.png';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { totalItems } = useCart();
  const isSettingsMode = location.pathname.startsWith('/settings');

  const isActive = (path: string) => location.pathname === path;

  // Header Component for the Sidebar
  const SidebarHeader = ({ label, icon: Icon }: { label: string, icon?: any }) => (
    <div className={`h-12 flex items-center bg-white dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-[#262626] ${collapsed ? 'justify-center' : 'px-5'}`}>
      {!collapsed ? (
         <div className="flex items-center gap-3">
           <BrandLogo type="main" size={20} />
           <div className="flex flex-col">
             <span className="text-xs font-bold tracking-widest text-[#161616] dark:text-white uppercase">{label}</span>
           </div>
         </div>
      ) : (
        <BrandLogo type="main" size={24} />
      )}
    </div>
  );

  // Settings Sidebar View - Triggered when URL is /settings/*
  if (isSettingsMode) {
    return (
      <div className={`flex flex-col h-screen bg-white dark:bg-[#0A0A0A] border-r border-gray-200 dark:border-[#262626] text-[#161616] dark:text-gray-200 ${TRANSITIONS} ${collapsed ? 'w-[3.5rem]' : 'w-[17rem]'} flex-shrink-0 z-20 overflow-hidden`}>
        <SidebarHeader label="System Settings" />

        <nav className="flex-1 overflow-y-auto custom-scrollbar overflow-x-hidden py-4">
          <div className="space-y-0.5">
            {/* Back to Dashboard Button */}
            <button
              onClick={() => navigate('/')}
              className={`w-full flex items-center h-10 mb-4 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'pl-5 pr-4'} text-gray-500 hover:text-brand-dark dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#1A1A1A]`}
            >
              <ArrowLeft size={18} className={collapsed ? '' : 'mr-3'} />
              {!collapsed && <span className="text-[10px] font-bold uppercase tracking-widest">{t('back')}</span>}
            </button>

            <div className={`px-5 mb-2 ${collapsed ? 'hidden' : ''}`}>
               <span className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">Administration</span>
            </div>

            <button
              onClick={() => navigate('/settings/general')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/settings/general') 
                  ? 'bg-brand-light dark:bg-[#161616] font-bold text-brand-dark dark:text-white border-l-[3px] border-brand-medium' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-brand-dark'
              }`}
            >
              <div className="flex items-center">
                <User size={18} className={`${collapsed ? '' : 'mr-3'}`} />
                {!collapsed && <span className="text-sm font-medium">{t('general_settings')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/settings/users')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                location.pathname.includes('/settings/users') 
                  ? 'bg-brand-light dark:bg-[#161616] font-bold text-brand-dark dark:text-white border-l-[3px] border-brand-medium' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-brand-dark'
              }`}
            >
              <div className="flex items-center">
                <Users size={18} className={`${collapsed ? '' : 'mr-3'}`} />
                {!collapsed && <span className="text-sm font-medium">{t('users_settings')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/settings/plan')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/settings/plan') 
                  ? 'bg-brand-light dark:bg-[#161616] font-bold text-brand-dark dark:text-white border-l-[3px] border-brand-medium' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-brand-dark'
              }`}
            >
              <div className="flex items-center">
                <CreditCard size={18} className={`${collapsed ? '' : 'mr-3'}`} />
                {!collapsed && <span className="text-sm font-medium">{t('plan_billing')}</span>}
              </div>
            </button>
          </div>
        </nav>

        <div className={`h-12 border-t border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0A0A0A] flex items-center justify-center`}>
          <span className="text-[10px] text-gray-500 dark:text-gray-500 font-bold uppercase tracking-widest whitespace-nowrap">
            {collapsed ? 'v2' : 'Settings Mode'}
          </span>
        </div>
      </div>
    );
  }

  // Render Sidebar for Supplier Role
  if (user?.role === 'supplier') {
    return (
      <div className={`flex flex-col h-screen bg-white dark:bg-[#0A0A0A] border-r border-gray-200 dark:border-[#262626] text-[#161616] dark:text-gray-200 ${TRANSITIONS} ${collapsed ? 'w-[3.5rem]' : 'w-[17rem]'} flex-shrink-0 z-20 overflow-hidden`}>
        <SidebarHeader label="Supplier Portal" />

        <nav className="flex-1 overflow-y-auto custom-scrollbar overflow-x-hidden py-4">
          <div className="space-y-0.5">
            <button
              onClick={() => navigate('/supplier-dashboard')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-dashboard') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Home size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-dashboard') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">Home</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/supplier-customers')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-customers') || location.pathname.startsWith('/supplier-customers')
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Users size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-customers') || location.pathname.startsWith('/supplier-customers') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">My Customers</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/supplier-products')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-products') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Package size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-products') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">{t('my_products')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/supplier-order-requests')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-order-requests') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <LayoutList size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-order-requests') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">Order Requests</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/supplier-cart')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-cart') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center relative">
                <ShoppingCart size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-cart') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">{t('cart')}</span>}
                {totalItems > 0 && (
                  <span className={`absolute ${collapsed ? '-top-1 -right-1' : '-top-1 left-3'} min-w-[16px] h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] font-black px-1 border border-white dark:border-black`}>
                    {totalItems}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => navigate('/supplier-orders')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/supplier-orders') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <List size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/supplier-orders') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">Order History</span>}
              </div>
            </button>
          </div>
        </nav>

        <div className={`h-12 border-t border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0A0A0A] flex items-center justify-center`}>
          <span className="text-[10px] text-gray-500 dark:text-gray-500 font-bold uppercase tracking-widest whitespace-nowrap">
            {collapsed ? 'SP' : user.companyName}
          </span>
        </div>
      </div>
    );
  }

  // Render Sidebar for Enterprise Role (Default Operational View)
  return (
    <>
      <div className={`flex flex-col h-screen bg-white dark:bg-[#0A0A0A] border-r border-gray-200 dark:border-[#262626] text-[#161616] dark:text-gray-200 ${TRANSITIONS} ${collapsed ? 'w-[3.5rem]' : 'w-[17rem]'} flex-shrink-0 z-20 overflow-hidden`}>
        <SidebarHeader label="VMI Hub" />

        <nav className="flex-1 overflow-y-auto custom-scrollbar overflow-x-hidden py-4">
          <div className="space-y-0.5">
            <button
              onClick={() => navigate('/')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <BarChart3 size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">{t('dashboard')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/stock')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/stock') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <List size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/stock') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">{t('stock_list')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/replenishment')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/replenishment') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <RefreshCw size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/replenishment') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">{t('replenishment')}</span>}
              </div>
            </button>

            <button
              onClick={() => navigate('/request-cart')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/request-cart') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center relative">
                <ShoppingCart size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/request-cart') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">Cart</span>}
                {totalItems > 0 && (
                  <span className={`absolute ${collapsed ? '-top-1 -right-1' : '-top-1 left-3'} min-w-[16px] h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] font-black px-1 border border-white dark:border-black`}>
                    {totalItems}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => navigate('/orders')}
              className={`w-full flex items-center h-10 ${TRANSITIONS} ${collapsed ? 'justify-center px-0' : 'justify-between pl-5 pr-4'} ${
                isActive('/orders') 
                  ? 'bg-gray-100 dark:bg-[#161616] font-bold text-[#161616] dark:text-white border-l-[3px] border-[#474D22]' 
                  : 'border-l-[3px] border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] hover:text-[#161616] dark:hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <List size={20} strokeWidth={2} className={`${collapsed ? '' : 'mr-3'} ${isActive('/orders') ? 'text-[#474D22] dark:text-white' : ''}`} />
                {!collapsed && <span className="text-sm font-medium">Order Requests</span>}
              </div>
            </button>
          </div>
        </nav>

        <div className="h-12 border-t border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
          {/* <span className="text-[10px] text-gray-500 dark:text-gray-500 font-bold uppercase tracking-widest whitespace-nowrap">
            {collapsed ? 'ENT' : 'Enterprise'}
          </span> */}
          <img
            className="calc_img"
            src={footerImage}
            style={{ height: 35 }}
            alt="logo"
          />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
